import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import dayjs from 'dayjs';

const STATUS_LABELS = {
  confirmed: 'Bestätigt',
  canceled: 'Storniert',
  noShow: 'No-Show',
  awaitingConfirmation: 'Wartet auf Bestätigung',
  rejected: 'Abgelehnt',
  waitingForPayment: 'Wartet auf Zahlung',
  blocked: 'Blockiert',
};

// Shows the BookingGroup ("Warenkorb") a booking belongs to: the meta booking
// number, whether all member bookings share the exact same time slot or at least
// the same day (including the combined time frame), and all sibling bookings with
// direct navigation into their detail view.
function BookingGroupSection({ bookingGroup, currentBookingId, onOpenSibling }) {
  if (!bookingGroup || !Array.isArray(bookingGroup.bookings) || bookingGroup.bookings.length < 2) {
    return null;
  }

  const timeFrameLabel =
    bookingGroup.startTime && bookingGroup.endTime
      ? `${dayjs(bookingGroup.startTime).format('DD.MM.YYYY HH:mm')} - ${dayjs(
          bookingGroup.endTime
        ).format('HH:mm')} Uhr`
      : null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 1.5,
        mb: 2,
        backgroundColor: 'action.hover',
      }}
    >
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap mb={1}>
        <ShoppingCartOutlinedIcon fontSize='small' color='action' />
        <Typography variant='body1'>
          <b>Warenkorb-Buchung:</b> {bookingGroup.groupBookingNumber}
        </Typography>
        {bookingGroup.isSameTimestamp ? (
          <Chip size='small' color='primary' variant='outlined' label='Gleicher Zeitpunkt' />
        ) : bookingGroup.isSameDay ? (
          <Chip size='small' color='primary' variant='outlined' label='Gleicher Tag' />
        ) : (
          <Chip size='small' variant='outlined' label='Verschiedene Tage' />
        )}
      </Stack>
      {timeFrameLabel && (
        <Typography variant='body2' sx={{ mb: 1 }}>
          <b>Gesamtzeitraum:</b> {timeFrameLabel}
        </Typography>
      )}
      <Stack spacing={0.5}>
        {bookingGroup.bookings.map(sibling => {
          const isCurrent = String(sibling._id) === String(currentBookingId);
          const label = `${sibling.bookingNumber} – ${
            sibling.entertainmentLongName || sibling.entertainment
          } · ${dayjs(sibling.startTime).format('DD.MM.YYYY HH:mm')}-${dayjs(
            sibling.endTime
          ).format('HH:mm')} · ${sibling.participantCount} Pers.`;
          return (
            <Stack key={String(sibling._id)} direction='row' spacing={1} alignItems='center'>
              <Typography
                variant='body2'
                onClick={() => {
                  if (!isCurrent && typeof onOpenSibling === 'function') {
                    onOpenSibling(sibling._id);
                  }
                }}
                sx={
                  isCurrent
                    ? { fontWeight: 700 }
                    : {
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }
                }
              >
                {label}
              </Typography>
              <Chip
                size='small'
                variant='outlined'
                color={
                  ['canceled', 'rejected', 'noShow'].includes(sibling.status)
                    ? 'error'
                    : sibling.status === 'confirmed'
                      ? 'success'
                      : 'warning'
                }
                label={STATUS_LABELS[sibling.status] || sibling.status}
              />
              {isCurrent && <Chip size='small' label='Geöffnet' />}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

export default BookingGroupSection;
